import {
  isDefined,
  isNotEmpty,
  isNotDefined,
  NotificationListInputSchema,
  type NotificationItem,
  type NotificationListOutput,
  type NotificationReadInput,
  type NotificationSettingsUpdateInput,
  type NotificationSettingsOutput,
  type NotificationUnreadCountOutput,
} from '@terros/common'
import { type InfiniteData, type QueryClient, type QueryKey } from '@tanstack/react-query'

const operationsKey = ['notification-optimistic']
const centerKey = ['notification-center']
type Operation = { keys: string[] }
export type OptimisticChange = { rollback: VoidFunction; finish: () => Promise<void> }

// Operation entries have no initial data: an account reset clears their ownership before a late response can roll back.
async function begin(
  client: QueryClient,
  keys: string[]
): Promise<{ active: () => boolean; finish: () => Promise<void> }> {
  const pending = client.getQueriesData<Operation>({ queryKey: operationsKey })
  if (pending.some(([, operation]) => operation?.keys.some((key) => keys.includes(key))))
    throw new Error('This item is still updating. Please try again.')
  const key = [...operationsKey, crypto.randomUUID()]
  const owner = client.setQueryData(key, { keys })
  const active = (): boolean => client.getQueryData(key) === owner
  await client.cancelQueries({ queryKey: centerKey })
  return {
    active,
    finish: async (): Promise<void> => {
      const owned = active()
      client.removeQueries({ queryKey: key, exact: true })
      if (owned && !client.getQueriesData<Operation>({ queryKey: operationsKey }).some(([, entry]) => isDefined(entry)))
        await client.invalidateQueries({ queryKey: centerKey })
    },
  }
}

export async function coordinateNotificationItemUpdate(client: QueryClient, ids: string[]): Promise<OptimisticChange> {
  const operation = await begin(
    client,
    ids.map((id) => `item:${id}`)
  )
  return { rollback: (): void => {}, finish: operation.finish }
}

function adjustCount(client: QueryClient, delta: number): number {
  let applied = 0
  client.setQueryData<NotificationUnreadCountOutput>([...centerKey, 'count'], (old) => {
    if (isNotDefined(old)) return old
    const count = Math.max(0, old.count + delta)
    applied = count - old.count
    return { ...old, count, display: count > 99 ? '99+' : String(count) }
  })
  return applied
}

export async function optimisticallyRead(client: QueryClient, input: NotificationReadInput): Promise<OptimisticChange> {
  const ids = input.all === true ? [] : input.ids
  const operation = await begin(
    client,
    ids.map((id) => `item:${id}`)
  )
  const before = new Map<string, NotificationItem>()
  const queries = client.getQueriesData<InfiniteData<NotificationListOutput>>({ queryKey: [...centerKey, 'list'] })
  for (const [, data] of queries)
    for (const page of data?.pages ?? [])
      for (const item of page.items) if (ids.includes(item.id)) before.set(item.id, item)
  const readAt = input.read ? Date.now() : null
  const delta = [...before.values()]
    .filter((item) => !item.expired && isNotDefined(item.state.archivedAt))
    .reduce((sum, item) => sum + Number(isNotDefined(readAt)) - Number(isNotDefined(item.state.readAt)), 0)
  const inserted = new Map<QueryKey, Set<string>>()
  let countDelta = 0
  if (operation.active()) {
    for (const [key, data] of queries) {
      if (isNotDefined(data)) continue
      const filter = NotificationListInputSchema.safeParse(key[2])
      const existing = new Set(data.pages.flatMap((page) => page.items.map((item) => item.id)))
      const additions = [...before.values()].filter(
        (item) =>
          !input.read &&
          !item.expired &&
          isNotDefined(item.state.archivedAt) &&
          !existing.has(item.id) &&
          filter.success &&
          filter.data.view === 'unread' &&
          (isNotDefined(filter.data.topic) || item.topic === filter.data.topic)
      )
      inserted.set(key, new Set(additions.map((item) => item.id)))
      client.setQueryData<InfiniteData<NotificationListOutput>>(key, {
        ...data,
        pages: data.pages.map((page, index) => {
          const items = [...page.items, ...(index === 0 ? additions : [])]
          if (index === 0 && isNotEmpty(additions)) items.sort((left, right) => right.id.localeCompare(left.id))
          return {
            ...page,
            items: items.map((item) => (ids.includes(item.id) ? { ...item, state: { ...item.state, readAt } } : item)),
          }
        }),
      })
    }
    countDelta = adjustCount(client, delta)
  }
  return {
    finish: operation.finish,
    rollback: (): void => {
      if (!operation.active()) return
      for (const [key] of queries) {
        client.setQueryData<InfiniteData<NotificationListOutput>>(
          key,
          (data) =>
            data && {
              ...data,
              pages: data.pages.map((page) => ({
                ...page,
                items: page.items
                  .filter((item) => !inserted.get(key)?.has(item.id))
                  .map((item) => {
                    const original = before.get(item.id)
                    return isDefined(original)
                      ? { ...item, state: { ...item.state, readAt: original.state.readAt } }
                      : item
                  }),
              })),
            }
        )
      }
      adjustCount(client, -countDelta)
    },
  }
}

export async function optimisticallyMute(
  client: QueryClient,
  input: NotificationSettingsUpdateInput
): Promise<OptimisticChange> {
  const mute = input.mute
  const sources = mute?.sources ?? []
  const operation = await begin(
    client,
    sources.map((source) => `mute:${source.kind}:${source.id}`)
  )
  const key = [...centerKey, 'settings']
  const before = client.getQueryData<NotificationSettingsOutput>(key)
  const matches = (source: { kind: string; id: string }): boolean =>
    sources.some((entry) => entry.kind === source.kind && entry.id === source.id)
  if (operation.active() && isDefined(mute))
    client.setQueryData<NotificationSettingsOutput>(
      key,
      (old) =>
        old && {
          ...old,
          mutedSources: [...old.mutedSources.filter((source) => !matches(source)), ...(mute.muted ? sources : [])],
        }
    )
  return {
    finish: operation.finish,
    rollback: (): void => {
      if (!operation.active() || isNotDefined(before)) return
      client.setQueryData<NotificationSettingsOutput>(
        key,
        (old) =>
          old && {
            ...old,
            mutedSources: [
              ...old.mutedSources.filter((source) => !matches(source)),
              ...before.mutedSources.filter(matches),
            ],
          }
      )
    },
  }
}
